import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Inline widget for resolving part duplicate conflicts.
 * Shows differences between existing and incoming part, with resolution buttons.
 *
 * Props:
 *   existingPart - existing part object from the backend
 *   incomingPart - incoming part data from extraction
 *   differences - { price?: { existing, incoming }, manufacturer_name?: { existing, incoming } }
 *   onUseExisting - callback when user chooses to use existing part
 *   onUpdateExisting - callback when user chooses to update existing part
 *   disabled - disable buttons during processing
 */
export default function DuplicatePartResolver({
  existingPart,
  incomingPart,
  differences,
  onUseExisting,
  onUpdateExisting,
  disabled = false,
}) {
  const { t } = useTranslation();

  const code = existingPart?.primary_code || existingPart?.manufacturer_code || incomingPart?.primary_code || incomingPart?.manufacturer_code || '';

  return (
    <Box
      sx={{
        backgroundColor: '#fff8e1',
        border: '1px solid #ffe082',
        borderRadius: 1,
        p: 2,
        my: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WarningAmberIcon sx={{ color: '#f9a825' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('partDuplicate.title')}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
        {t('partDuplicate.description', { code })}
      </Typography>

      <Table size="small" sx={{ mb: 1.5, '& td': { py: 0.5, px: 1, border: 'none' } }}>
        <TableBody>
          {/* Header row */}
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: '33%' }}></TableCell>
            <TableCell sx={{ fontWeight: 600, width: '33%' }}>
              {t('partDuplicate.existing')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '33%' }}>
              {t('partDuplicate.incoming')}
            </TableCell>
          </TableRow>
          {differences.price && (
            <TableRow>
              <TableCell sx={{ color: 'text.secondary' }}>
                {t('partDuplicate.field_price')}
              </TableCell>
              <TableCell>{differences.price.existing ?? '-'}</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>
                {differences.price.incoming ?? '-'}
              </TableCell>
            </TableRow>
          )}
          {differences.manufacturer_name && (
            <TableRow>
              <TableCell sx={{ color: 'text.secondary' }}>
                {t('partDuplicate.field_manufacturer_name')}
              </TableCell>
              <TableCell>{differences.manufacturer_name.existing ?? '-'}</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>
                {differences.manufacturer_name.incoming ?? '-'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onUseExisting}
          disabled={disabled}
          startIcon={disabled ? <CircularProgress size={14} /> : null}
        >
          {t('partDuplicate.useExisting')}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="warning"
          onClick={onUpdateExisting}
          disabled={disabled}
          startIcon={disabled ? <CircularProgress size={14} /> : null}
        >
          {t('partDuplicate.updateExisting')}
        </Button>
      </Box>
    </Box>
  );
}
